import {Injectable} from '@angular/core';
import Dexie from "dexie";
import {Inventory} from "../shared/interfaces/Inventory";
import {FullConstat} from "../shared/interfaces/FullConstat";
import {Constat} from "../shared/interfaces/Constat";

interface LoginResult {
  username: string;
  role: string;
  accessToken: string;
  refreshToken: string;
  fullname: string;
  id: string;
  apiKey: string;
  ssn: string;
}

@Injectable({
  providedIn: 'root'
})
export class IndexedDbService extends Dexie {
  public user: Dexie.Table<LoginResult, string>;
  public constats: Dexie.Table<Inventory, string>;
  public fullConstats: Dexie.Table<FullConstat, string>;
  public selectedResidency: Dexie.Table<Constat, string>;
  public inventoryInProgress: Dexie.Table<FullConstat, string>;
  public fileToSend: Dexie.Table<any, string>;
  public oldFiles: Dexie.Table<any, string>;

  constructor() {
    super('bnp');

    // Définir le schéma de la base de données
    this.version(1).stores({
      user: 'ssn',
      constats: 'id',
      fullConstats: 'id',
      selectedResidency: 'selectedResidency',
      inventoryInProgress: 'id',
      fileToSend: 'name',
      oldFiles: 'path,previewUrl'

    });

    // Accéder aux tables
    this.user = this.table('user');
    this.constats = this.table('constats');
    this.fullConstats = this.table('fullConstats');
    this.selectedResidency = this.table('selectedResidency');
    this.inventoryInProgress = this.table('inventoryInProgress');
    this.fileToSend = this.table('fileToSend');
    this.oldFiles = this.table('oldFiles');
  }

  async getDatasFromIndexDb(table: string, key: string, attributes?: string): Promise<any | null> {
    try {
      let datas
      switch (table) {
        case "user":
          await this.user.get(key).then((user: any) => {
            if (attributes) {
              datas = user[attributes];
            } else {
              datas = user;
            }
          })
          break;
        case "fullConstats":
          await this.fullConstats.get(key).then((fullConstat: any) => {
            if (fullConstat) {
              if (attributes) {
                datas = fullConstat[attributes];
              } else {
                datas = fullConstat;
              }
            }
          })
          break;
        case "selectedResidency":
          await this.selectedResidency.get(key).then((selectedResidency: any) => {
            if (attributes) {
              datas = selectedResidency[attributes];
            } else {
              datas = selectedResidency;
            }
          })
          break;
        case "constats":
          await this.constats.get(key).then((constat: any) => {
            if (attributes) {
              datas = constat[attributes];
            } else {
              datas = constat;
            }
          })
          break;
        case "inventoryInProgress":
          await this.inventoryInProgress.get(key).then((inventoryInProgress: any) => {
            if (attributes) {
              datas = inventoryInProgress[attributes];
            } else {
              datas = inventoryInProgress;
            }
          })
          break;
        case "fileToSend":
          await this.fileToSend.get(key).then((fileToSend: any) => {
            if (attributes) {
              datas = fileToSend[attributes];
            } else {
              datas = fileToSend;
            }
          })
          break;
        case "oldFiles":
          await this.oldFiles.get(key).then((oldFiles: any) => {
            if (attributes) {
              datas = oldFiles[attributes];
            } else {
              datas = oldFiles;
            }
          })
          break;
        default:
          break
      }
      return datas
    } catch (error) {
      console.error('Error retrieving datas from IndexedDB', error);
      return null;
    }
  }


  async getAllDatasFromIndexDb(table: string) {
    try {
      let allDatasFromTable
      switch (table) {
        case "fullConstats":
          allDatasFromTable = await this.fullConstats.toArray();
          break;
        case "selectedResidency":
          allDatasFromTable = await this.selectedResidency.toArray();
          break;
        case "constats":
          allDatasFromTable = await this.constats.toArray();
          break;
        case "inventoryInProgress":
          allDatasFromTable = await this.inventoryInProgress.toArray();
          break;
        case "fileToSend":
          allDatasFromTable = await this.fileToSend.toArray();
          break;
        default:
          break;
      }
      return allDatasFromTable;
    } catch (error) {
      console.error('Erreur lors de la récupération de tous les objets de la table fullConstats', error);
      return null;
    }
  }

  async removeDataFromTable(tableName: string): Promise<void> {
    try {
      switch (tableName) {
        case 'user':
          await this.user.clear();
          break;
        case 'constats':
          await this.constats.clear();
          break;
        case 'fullConstats':
          await this.fullConstats.clear();
          break;
        case 'selectedResidency':
          await this.selectedResidency.clear();
          break;
        case 'inventoryInProgress':
          await this.inventoryInProgress.clear();
          break;
        default:
          throw new Error(`Table ${tableName} not found`);
      }
    } catch (error) {
      console.error(`Error removing data from ${tableName} in IndexedDB`, error);
      throw error;
    }
  }

  async deleteAndReplace(table: string, id: string, newObject: any) {
    switch (table) {
      case 'fullConstats':
        await this.transaction('rw', this.fullConstats, async () => {
          // Supprimez l'ancien constat
          await this.fullConstats.where('id').equals(id).delete();
          // Ajoutez le nouveau constat
          if (newObject) {
            await this.fullConstats.add(newObject);
          }
        });
        break
      case 'inventoryInProgress':
        await this.transaction('rw', this.inventoryInProgress, async () => {
          await this.inventoryInProgress.where('id').equals(id).delete();
          if (newObject) {
            await this.inventoryInProgress.add(newObject);
          }
        });
        break
      case 'user':
        await this.transaction('rw', this.user, async () => {
          await this.user.where('ssn').equals(id).delete();
          if (newObject) {
            await this.user.add(newObject);
          }
        });
        break
      case 'constats':
        await this.transaction('rw', this.constats, async () => {
          await this.constats.where('id').equals(id).delete();
          if (newObject) {
            await this.constats.add(newObject);
          }
        });
        break
      default:
        break
    }
  }

  async deleteFilesByArrayName(names: string[]) {
    await this.transaction('rw', this.fileToSend, async () => {
      for (const name of names) {
        await this.fileToSend.where('name').equals(name).delete();
      }
    });
  }

  async getConstatByLineId(idLine: any) {
    const res = await this.getAllDatasFromIndexDb("fullConstats");

    if (!res || !Array.isArray(res)) return null;

    return res.find((constat: any) =>
      constat.lignes?.find((ligne: any) => ligne.id === idLine)
    );
  }

  async deleteDatabase(): Promise<void> {
    try {
      const dbDeleted = await Dexie.delete('bnp');
      location.reload();
      console.log('BNP database deleted successfully:', dbDeleted);
    } catch (error) {
      console.error('Error deleting BNP database:', error);
    }
  }

}
